import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertTriangle,
  Phone,
  Mail,
  MapPin,
  DollarSign,
  Calendar,
  ChevronRight,
  Filter,
  RefreshCcw,
  Download,
  Search
} from 'lucide-react';
import { logger } from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CollectionsWorklistPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [reps, setReps] = useState([]);
  
  // Filters
  const [selectedBucket, setSelectedBucket] = useState(searchParams.get('bucket') || 'all');
  const [selectedRep, setSelectedRep] = useState(searchParams.get('rep') || 'all');
  const [sortBy, setSortBy] = useState('days_overdue');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchWorklist = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedBucket !== 'all') params.append('bucket', selectedBucket);
      if (selectedRep !== 'all') params.append('rep_id', selectedRep);
      params.append('sort_by', sortBy);
      params.append('limit', '200');
      
      const response = await fetch(
        `${API_URL}/api/intelligence/collections-worklist?${params}`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (error) {
      logger.error('Error fetching worklist:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedBucket, selectedRep, sortBy]);

  const fetchReps = async () => {
    try {
      const response = await fetch(`${API_URL}/api/users`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setReps(data.filter(u => ['sales_rep', 'sales_manager', 'admin', 'owner', 'account_executive'].includes(u.role)));
      }
    } catch (error) {
      logger.error('Error fetching reps:', error);
    }
  };

  useEffect(() => {
    fetchReps();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchWorklist();
  }, [fetchWorklist]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter worklist by search query
  const filteredWorklist = data?.worklist?.filter(item => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      item.customer_name?.toLowerCase().includes(query) ||
      item.customer_phone?.toLowerCase().includes(query) ||
      item.file_id?.toLowerCase().includes(query) ||
      item.project_name?.toLowerCase().includes(query)
    );
  }) || [];

  const formatCurrency = (amount) => {
    if (!amount) return '$0';
    return `$${amount.toLocaleString()}`;
  };

  const bucketColors = {
    '0_30': 'bg-green-100 text-green-800',
    '31_60': 'bg-yellow-100 text-yellow-800',
    '61_90': 'bg-orange-100 text-orange-800',
    '90_plus': 'bg-red-100 text-red-800'
  };

  const priorityColors = {
    'critical': 'bg-red-500 text-white',
    'high': 'bg-orange-500 text-white',
    'medium': 'bg-yellow-500 text-slate-900'
  };

  return (
    <div className="p-6 space-y-6 bg-slate-50 min-h-screen" data-testid="collections-worklist-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <AlertTriangle className="h-6 w-6 text-red-500" />
            Collections Worklist
          </h1>
          <p className="text-sm text-slate-500">Overdue installments requiring follow-up</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={fetchWorklist} disabled={loading}>
            <RefreshCcw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="p-4">
            <div className="text-xs text-red-600 mb-1">Total Overdue</div>
            <div className="text-2xl font-bold text-red-700">{formatCurrency(data?.total_balance)}</div>
            <div className="text-xs text-red-500">{data?.total_count || 0} items</div>
          </CardContent>
        </Card>
        
        {data?.by_bucket && Object.entries(data.by_bucket).map(([bucket, info]) => (
          <Card 
            key={bucket} 
            className={`cursor-pointer hover:shadow-md transition-shadow ${selectedBucket === bucket ? 'ring-2 ring-blue-500' : ''}`}
            onClick={() => setSelectedBucket(bucket)}
          >
            <CardContent className="p-4">
              <div className="text-xs text-slate-500 mb-1">
                {{'0_30': '0-30 Days', '31_60': '31-60 Days', '61_90': '61-90 Days', '90_plus': '90+ Days'}[bucket]}
              </div>
              <div className="text-xl font-bold">{formatCurrency(info.amount)}</div>
              <div className="text-xs text-slate-400">{info.count} items</div>
            </CardContent>
          </Card>
        ))}
        
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-4">
            <div className="text-xs text-purple-600 mb-1">Critical Priority</div>
            <div className="text-2xl font-bold text-purple-700">{data?.by_priority?.critical || 0}</div>
            <div className="text-xs text-purple-500">Need immediate action</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-2">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5">
              <Filter className="h-4 w-4 text-slate-400" />
              <span className="text-xs font-medium text-slate-600">Filters:</span>
            </div>
            
            <Select value={selectedBucket} onValueChange={setSelectedBucket}>
              <SelectTrigger className="w-32 h-8">
                <SelectValue placeholder="All Buckets" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Buckets</SelectItem>
                <SelectItem value="0_30">0-30 Days</SelectItem>
                <SelectItem value="31_60">31-60 Days</SelectItem>
                <SelectItem value="61_90">61-90 Days</SelectItem>
                <SelectItem value="90_plus">90+ Days</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={selectedRep} onValueChange={setSelectedRep}>
              <SelectTrigger className="w-40 h-8">
                <SelectValue placeholder="All Reps" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Reps</SelectItem>
                {reps.map((rep) => (
                  <SelectItem key={rep.user_id} value={rep.user_id}>{rep.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-36 h-8">
                <SelectValue placeholder="Sort By" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="days_overdue">Days Overdue</SelectItem>
                <SelectItem value="balance">Balance Amount</SelectItem>
                <SelectItem value="priority">Priority Score</SelectItem>
              </SelectContent>
            </Select>
            
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search customer, phone, file..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-8"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Worklist Table */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">
              {filteredWorklist.length} Items
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-16 w-full" />)}
            </div>
          ) : filteredWorklist.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Priority</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>File / Project</TableHead>
                    <TableHead>Rep</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                    <TableHead className="text-right">Days</TableHead>
                    <TableHead>Bucket</TableHead>
                    <TableHead>Last Contact</TableHead>
                    <TableHead>Last Payment</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredWorklist.map((item) => (
                    <TableRow key={item.installment_id} className="hover:bg-slate-50">
                      <TableCell>
                        <Badge className={priorityColors[item.priority]}>
                          {item.priority}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{item.customer_name}</div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {item.customer_phone && (
                            <a 
                              href={`tel:${item.customer_phone}`} 
                              className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
                            >
                              <Phone className="h-3 w-3" /> {item.customer_phone}
                            </a>
                          )}
                          {item.customer_email && (
                            <a 
                              href={`mailto:${item.customer_email}`}
                              className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
                            >
                              <Mail className="h-3 w-3" /> {item.customer_email}
                            </a>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-xs">
                          <div className="font-mono text-slate-600">{item.file_id}</div>
                          <div className="text-slate-400">{item.project_name}</div>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{item.rep_name}</TableCell>
                      <TableCell className="text-right">
                        <div className="font-bold text-red-600">{formatCurrency(item.balance_amount)}</div>
                        <div className="text-xs text-slate-400">of {formatCurrency(item.due_amount)}</div>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={`font-medium ${item.days_overdue > 60 ? 'text-red-600' : item.days_overdue > 30 ? 'text-orange-600' : 'text-yellow-600'}`}>
                          {item.days_overdue}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge className={bucketColors[item.bucket]}>
                          {item.bucket_label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs">
                        {item.last_contact ? (
                          <div>
                            <div className="text-slate-600">{item.last_contact.channel}</div>
                            <div className="text-slate-400">
                              {new Date(item.last_contact.date).toLocaleDateString()}
                            </div>
                          </div>
                        ) : (
                          <span className="text-slate-400">No contact</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">
                        {item.last_payment?.date ? (
                          <div>
                            <div className="text-green-600">{formatCurrency(item.last_payment.amount)}</div>
                            <div className="text-slate-400">
                              {new Date(item.last_payment.date).toLocaleDateString()}
                            </div>
                          </div>
                        ) : (
                          <span className="text-red-400">Never paid</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => navigate(`/contracts/${item.contract_id}`)}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12">
              <DollarSign className="h-12 w-12 mx-auto mb-4 text-green-500" />
              <p className="text-slate-500">No overdue items found</p>
              <p className="text-sm text-slate-400">Great job keeping collections current!</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
